import React, {useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flex, flexDirection, mt, p, pb, pl, position, pr, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {useFetch, useIsMounted, useWindowSize} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {propertyOffer} from "@pg-mono/string-utils";

import {slickSlider} from "../../../app/styles/slick_slider";
import {getAdjustedSliderSettings} from "../../../app/utils/get_adjusted_slider_settings";
import {MoreOffersBox} from "../../../homepage/components/OffersPanel/MoreOffersBox";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {hitGTMOfferViewItemList} from "../../../offer/tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {updateLocalStoragePropertyFilters} from "../../../search/utils/use_localstorage_property_filters";
import {ILatestQuery} from "../../../utils/latest_query";
import {fetchRecommendedOffers, FetchRecommendedOffersResponse} from "../../api/fetch_recommended_offers";
import {slickSliderArticleSettings} from "../../constants/slick_slider_article_settings";
import {gtmArticleOfferSliderHit} from "../../tracking/article_offer_slider_events";

const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

interface IProps {
    dataUrl: string;
}

export const ArticleOfferSlider = (props: IProps) => {
    const windowSize = useWindowSize();
    const [activeSlide, setActiveSlide] = useState(0);
    const isMounted = useIsMounted();
    const {response, fetchState, fetchStart} = useFetch<FetchRecommendedOffersResponse>(() => fetchRecommendedOffers(props.dataUrl));
    const isLoading = fetchState === RequestState.Waiting;

    const moreOffersText =
        response?.data.count && response.data.count - response.data.results.length
            ? `Zobacz jeszcze ${response.data.count - response.data.results.length} ${propertyOffer.accusative(response.data.count - response.data.results.length)}`
            : "";

    const showMoreOffers = !!response?.data.count && !!(response.data.count - response.data.results.length);

    const itemCount = (response?.data.results.length ?? 0) + Number(showMoreOffers);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderArticleSettings, itemCount), [itemCount]);

    // based on wheter user is on tablet and above or not we should subtract different slide count
    const slidesToSubtract = !!windowSize.width && windowSize.width > 768 ? 2 : 1;
    const isOnLastSlide = response ? activeSlide === response?.data.results?.length + Number(showMoreOffers) - slidesToSubtract : false;

    const handleBeforeChange = (prev: number, next: number) => {
        setActiveSlide(next);
        gtmArticleOfferSliderHit(prev > next ? "carousel_left_arrow" : "carousel_right_arrow");
    };

    useEffect(() => {
        fetchStart();
    }, []);

    const gtmOfferMeta = {
        listId: "oferty_promowane_w_artykule",
        listName: "Oferty promowane w artykule"
    };
    useEffect(() => {
        if (response?.data.results.length) {
            hitGTMOfferViewItemList({
                offers: response.data.results,
                ...gtmOfferMeta
            });
        }
    }, [response?.data.results]);

    if (!isMounted) {
        return <Loader />;
    }

    if (isMounted && !isLoading && !response?.data.count) {
        return <></>;
    }

    const queryValueToSave = {
        ...(response?.parsedQuery?.rooms_0 ? {rooms_0: response?.parsedQuery.rooms_0} : {}),
        ...(response?.parsedQuery?.rooms_1 ? {rooms_1: response?.parsedQuery.rooms_1} : {}),
        ...(response?.parsedQuery?.area_0 ? {area_0: response?.parsedQuery.area_0} : {}),
        ...(response?.parsedQuery?.area_0 ? {area_0: response?.parsedQuery.area_0} : {}),
        ...(response?.parsedQuery?.price_1 ? {price_1: response?.parsedQuery.price_1} : {}),
        ...(response?.parsedQuery?.floor_choices ? {floor_choices: response?.parsedQuery.floor_choices} : {}),
        ...(response?.parsedQuery?.house_storeys ? {house_storeys: response?.parsedQuery.house_storeys} : {})
    };
    return (
        <>
            {isMounted && (
                <React.Suspense fallback={<Loader />}>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div className="article-offer-slider" css={position("relative")}>
                            <Text strong>Zobacz najpopularniejsze oferty {response?.region_name_declension_where}</Text>
                            {/* // TODO: move SlickWrapper style to unified one */}
                            <SlickWrapper isOnFirstSlide={activeSlide === 0} isOnLastSlide={isOnLastSlide}>
                                {/* TODO: fix slider right chevron moving a little when left chevron appears */}
                                <LazySlider {...adjustedSliderSettings} beforeChange={handleBeforeChange}>
                                    {response?.data.results.map((offer, index) => {
                                        return (
                                            <div key={`${offer.id}${index}`} css={offerBoxWrapper}>
                                                <OfferBoxWithOfferDetailsAction
                                                    offer={offer}
                                                    //SEO requirement
                                                    titleTag="div"
                                                    shouldLinkToOffer
                                                    onClick={() => {
                                                        updateLocalStoragePropertyFilters(queryValueToSave);

                                                        gtmArticleOfferSliderHit("carousel_offer_click");
                                                    }}
                                                    index={index}
                                                    gtmMeta={{
                                                        ...gtmOfferMeta,
                                                        source: "additional",
                                                        tileEvents: {
                                                            imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                            buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                            linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                            logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                        }
                                                    }}
                                                    latestQuery={response?.parsedQuery as ILatestQuery & Record<string, string | string[] | null>}
                                                />
                                            </div>
                                        );
                                    })}

                                    {showMoreOffers && (
                                        <div css={offerBoxWrapper}>
                                            <MoreOffersBox
                                                link={props.dataUrl}
                                                text={moreOffersText}
                                                onClick={() => gtmArticleOfferSliderHit("carousel_more_offers")}
                                            />
                                        </div>
                                    )}
                                </LazySlider>
                            </SlickWrapper>
                        </div>
                    )}
                </React.Suspense>
            )}
        </>
    );
};

const Loader = () => {
    return (
        <div css={skeletonWrapper}>
            <OfferSkeleton />
            <OfferSkeleton />
            <OfferSkeleton />
        </div>
    );
};

const offerBoxWrapper = (theme: Theme) => css`
    max-width: 302px;
    height: 100%;

    @media (min-width: ${theme.breakpoints.xs}) {
        min-width: 360px;
        max-width: 384px;
        ${pr(3)};
    }
`;

// TODO: Create atoms for building skeleton components
const OfferSkeleton = () => {
    return (
        <div css={[pr(3), flex("start", "flex-start")]}>
            <div css={offerSkeleton}>
                <div css={[skeletonImage, skeletonAnimation]} />
                <div css={skeletonTextContainer}>
                    <SkeletonParagraph rows={2} css={skeletonAnimation} />
                    <SkeletonParagraph rows={1} css={[w100, skeletonAnimation]} />
                    <SkeletonParagraph rows={4} css={[w100, skeletonAnimation]} />
                </div>
            </div>
        </div>
    );
};

export const skeletonAnimation = (theme: Theme) => css`
    animation: skeleton-loading 1s ease-in-out infinite alternate;

    @keyframes skeleton-loading {
        0% {
            background-color: ${theme.colors.gray[200]};
        }
        100% {
            background-color: ${theme.colors.gray[400]};
        }
    }
`;

const offerSkeleton = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    ${borderRadius(2)};
    ${elevation(2)};
    background-color: #fff;
    overflow: hidden;
    ${w100};
`;

interface ISkeletonParagraphProps {
    rows: number;
}

const skeletonTextContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")}
    ${p(2)};
    gap: ${calculateRemSize(1.5)};
    ${mt(2)};
    ${w100};
`;

const skeletonImage = (theme: Theme) => css`
    ${w100};
    min-height: 196px;
    width: 302px;

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 350px;
    }
`;

const SkeletonParagraph = styled.div<ISkeletonParagraphProps>`
    width: 65%;
    min-height: calc(${({rows}) => rows} * ${calculateRemSize(3)});
    ${borderRadius(1)};
`;

export const SlickWrapper = styled.div<{isOnFirstSlide: boolean; isOnLastSlide: boolean}>`
    ${slickSlider};
    ${position("relative")};
    left: -${calculateRemSize(3)};
    width: 100vw;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        width: 100%;
        left: initial;
        padding: 0;
    }

    .slick-slide {
        height: inherit;

        > div {
            height: 100%;
        }
    }

    .slick-list {
        ${pl(2)};
        ${pb(1.5)};

        @media (min-width: ${(props) => props.theme.breakpoints.md}) {
            padding-left: 0;

            &:after {
                ${(props) =>
                    !props.isOnLastSlide &&
                    css`
                        content: "";
                    `}
                width: 2.4rem;
                height: 100%;
                position: absolute;
                top: 0;
                right: -12px;
                background-color: ${(props) => props.theme.colors.gray["100"]};
            }
        }
    }

    .slick-track {
        margin: 1rem 0;
        display: flex;

        @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
            column-gap: 1.6rem;
        }

        @media (min-width: ${(props) => props.theme.breakpoints.xs}) {
            margin: 2rem 0 4rem 0;
        }
    }

    .slick-prev,
    .slick-next {
        display: none !important;

        @media (min-width: ${(props) => props.theme.breakpoints.md}) {
            display: block !important;
        }
    }

    .slick-prev {
        left: -12px;
    }

    .slick-next {
        right: -12px;
    }

    .slick-disabled {
        display: none !important;
    }
`;

const skeletonWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${w100};
    overflow: hidden;
    ${p(2.5, 0, 6, 0)};

    @media (min-width: ${theme.breakpoints.md}) {
        max-width: calc(100vw - ${calculateRemSize(8)});
    }
`;
import React, {useEffect, useMemo, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flex, flexDirection, mt, p, pb, pl, position, pr, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {useFetch, useIsMounted, useWindowSize} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {propertyOffer} from "@pg-mono/string-utils";

import {slickSlider} from "../../../app/styles/slick_slider";
import {getAdjustedSliderSettings} from "../../../app/utils/get_adjusted_slider_settings";
import {MoreOffersBox} from "../../../homepage/components/OffersPanel/MoreOffersBox";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {hitGTMOfferViewItemList} from "../../../offer/tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../offer/tracking/offer_box_gtm_events";
import {updateLocalStoragePropertyFilters} from "../../../search/utils/use_localstorage_property_filters";
import {ILatestQuery} from "../../../utils/latest_query";
import {fetchRecommendedOffers, FetchRecommendedOffersResponse} from "../../api/fetch_recommended_offers";
import {slickSliderArticleSettings} from "../../constants/slick_slider_article_settings";
import {gtmArticleOfferSliderHit} from "../../tracking/article_offer_slider_events";

const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

interface IProps {
    dataUrl: string;
}

export const ArticleOfferSlider = (props: IProps) => {
    const windowSize = useWindowSize();
    const [activeSlide, setActiveSlide] = useState(0);
    const isMounted = useIsMounted();
    const {response, fetchState, fetchStart} = useFetch<FetchRecommendedOffersResponse>(() => fetchRecommendedOffers(props.dataUrl));
    const isLoading = fetchState === RequestState.Waiting;

    const moreOffersText =
        response?.data.count && response.data.count - response.data.results.length
            ? `Zobacz jeszcze ${response.data.count - response.data.results.length} ${propertyOffer.accusative(response.data.count - response.data.results.length)}`
            : "";

    const showMoreOffers = !!response?.data.count && !!(response.data.count - response.data.results.length);

    const itemCount = (response?.data.results.length ?? 0) + Number(showMoreOffers);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderArticleSettings, itemCount), [itemCount]);

    // based on wheter user is on tablet and above or not we should subtract different slide count
    const slidesToSubtract = !!windowSize.width && windowSize.width > 768 ? 2 : 1;
    const isOnLastSlide = response ? activeSlide === response?.data.results?.length + Number(showMoreOffers) - slidesToSubtract : false;

    const handleBeforeChange = (prev: number, next: number) => {
        setActiveSlide(next);
        gtmArticleOfferSliderHit(prev > next ? "carousel_left_arrow" : "carousel_right_arrow");
    };

    useEffect(() => {
        fetchStart();
    }, []);

    const gtmOfferMeta = {
        listId: "oferty_promowane_w_artykule",
        listName: "Oferty promowane w artykule"
    };
    useEffect(() => {
        if (response?.data.results.length) {
            hitGTMOfferViewItemList({
                offers: response.data.results,
                ...gtmOfferMeta
            });
        }
    }, [response?.data.results]);

    if (!isMounted) {
        return <Loader />;
    }

    if (isMounted && !isLoading && !response?.data.count) {
        return <></>;
    }

    const queryValueToSave = {
        ...(response?.parsedQuery?.rooms_0 ? {rooms_0: response?.parsedQuery.rooms_0} : {}),
        ...(response?.parsedQuery?.rooms_1 ? {rooms_1: response?.parsedQuery.rooms_1} : {}),
        ...(response?.parsedQuery?.area_0 ? {area_0: response?.parsedQuery.area_0} : {}),
        ...(response?.parsedQuery?.area_0 ? {area_0: response?.parsedQuery.area_0} : {}),
        ...(response?.parsedQuery?.price_1 ? {price_1: response?.parsedQuery.price_1} : {}),
        ...(response?.parsedQuery?.floor_choices ? {floor_choices: response?.parsedQuery.floor_choices} : {}),
        ...(response?.parsedQuery?.house_storeys ? {house_storeys: response?.parsedQuery.house_storeys} : {})
    };
    return (
        <>
            {isMounted && (
                <React.Suspense fallback={<Loader />}>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div className="article-offer-slider" css={position("relative")}>
                            <Text strong>Zobacz najpopularniejsze oferty {response?.region_name_declension_where}</Text>
                            {/* // TODO: move SlickWrapper style to unified one */}
                            <SlickWrapper isOnFirstSlide={activeSlide === 0} isOnLastSlide={isOnLastSlide}>
                                {/* TODO: fix slider right chevron moving a little when left chevron appears */}
                                <LazySlider {...adjustedSliderSettings} beforeChange={handleBeforeChange}>
                                    {response?.data.results.map((offer, index) => {
                                        return (
                                            <div key={`${offer.id}${index}`} css={offerBoxWrapper}>
                                                <OfferBoxWithOfferDetailsAction
                                                    offer={offer}
                                                    //SEO requirement
                                                    titleTag="div"
                                                    shouldLinkToOffer
                                                    onClick={() => {
                                                        updateLocalStoragePropertyFilters(queryValueToSave);

                                                        gtmArticleOfferSliderHit("carousel_offer_click");
                                                    }}
                                                    index={index}
                                                    gtmMeta={{
                                                        ...gtmOfferMeta,
                                                        source: "additional",
                                                        tileEvents: {
                                                            imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                            buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                            linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                            logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                        }
                                                    }}
                                                    latestQuery={response?.parsedQuery as ILatestQuery & Record<string, string | string[] | null>}
                                                />
                                            </div>
                                        );
                                    })}

                                    {showMoreOffers && (
                                        <div css={offerBoxWrapper}>
                                            <MoreOffersBox
                                                link={props.dataUrl}
                                                text={moreOffersText}
                                                onClick={() => gtmArticleOfferSliderHit("carousel_more_offers")}
                                            />
                                        </div>
                                    )}
                                </LazySlider>
                            </SlickWrapper>
                        </div>
                    )}
                </React.Suspense>
            )}
        </>
    );
};

const Loader = () => {
    return (
        <div css={skeletonWrapper}>
            <OfferSkeleton />
            <OfferSkeleton />
            <OfferSkeleton />
        </div>
    );
};

const offerBoxWrapper = (theme: Theme) => css`
    max-width: 302px;
    height: 100%;

    @media (min-width: ${theme.breakpoints.xs}) {
        min-width: 360px;
        max-width: 384px;
        ${pr(3)};
    }
`;

// TODO: Create atoms for building skeleton components
const OfferSkeleton = () => {
    return (
        <div css={[pr(3), flex("start", "flex-start")]}>
            <div css={offerSkeleton}>
                <div css={[skeletonImage, skeletonAnimation]} />
                <div css={skeletonTextContainer}>
                    <SkeletonParagraph rows={2} css={skeletonAnimation} />
                    <SkeletonParagraph rows={1} css={[w100, skeletonAnimation]} />
                    <SkeletonParagraph rows={4} css={[w100, skeletonAnimation]} />
                </div>
            </div>
        </div>
    );
};

export const skeletonAnimation = (theme: Theme) => css`
    animation: skeleton-loading 1s ease-in-out infinite alternate;

    @keyframes skeleton-loading {
        0% {
            background-color: ${theme.colors.gray[200]};
        }
        100% {
            background-color: ${theme.colors.gray[400]};
        }
    }
`;

const offerSkeleton = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    ${borderRadius(2)};
    ${elevation(2)};
    background-color: #fff;
    overflow: hidden;
    ${w100};
`;

interface ISkeletonParagraphProps {
    rows: number;
}

const skeletonTextContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")}
    ${p(2)};
    gap: ${calculateRemSize(1.5)};
    ${mt(2)};
    ${w100};
`;

const skeletonImage = (theme: Theme) => css`
    ${w100};
    min-height: 196px;
    width: 302px;

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 350px;
    }
`;

const SkeletonParagraph = styled.div<ISkeletonParagraphProps>`
    width: 65%;
    min-height: calc(${({rows}) => rows} * ${calculateRemSize(3)});
    ${borderRadius(1)};
`;

export const SlickWrapper = styled.div<{isOnFirstSlide: boolean; isOnLastSlide: boolean}>`
    ${slickSlider};
    ${position("relative")};
    left: -${calculateRemSize(3)};
    width: 100vw;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        width: 100%;
        left: initial;
        padding: 0;
    }

    .slick-slide {
        height: inherit;

        > div {
            height: 100%;
        }
    }

    .slick-list {
        ${pl(2)};
        ${pb(1.5)};

        @media (min-width: ${(props) => props.theme.breakpoints.md}) {
            padding-left: 0;

            &:after {
                ${(props) =>
                    !props.isOnLastSlide &&
                    css`
                        content: "";
                    `}
                width: 2.4rem;
                height: 100%;
                position: absolute;
                top: 0;
                right: -12px;
                background-color: ${(props) => props.theme.colors.gray["100"]};
            }
        }
    }

    .slick-track {
        margin: 1rem 0;
        display: flex;

        @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
            column-gap: 1.6rem;
        }

        @media (min-width: ${(props) => props.theme.breakpoints.xs}) {
            margin: 2rem 0 4rem 0;
        }
    }

    .slick-prev,
    .slick-next {
        display: none !important;

        @media (min-width: ${(props) => props.theme.breakpoints.md}) {
            display: block !important;
        }
    }

    .slick-prev {
        left: -12px;
    }

    .slick-next {
        right: -12px;
    }

    .slick-disabled {
        display: none !important;
    }
`;

const skeletonWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${w100};
    overflow: hidden;
    ${p(2.5, 0, 6, 0)};

    @media (min-width: ${theme.breakpoints.md}) {
        max-width: calc(100vw - ${calculateRemSize(8)});
    }
`;
